interface FnolSteps {
  CLAIMANT_INFO: FnolStepsObj;
  CLAIM_TYPE_DETAILS: FnolStepsObj;
  PAYMENT_DETAILS: FnolStepsObj;
  UPLOAD_DOCS: FnolStepsObj;
  DECLARATION: FnolStepsObj;
}

interface FnolStepsObj {
  name: string;
  slNo: number;
  title: string;
  progressStepTitle: string;
}

interface FnolLiteStepList {
  CLAIMANT_INFO: FnolStepsObj;
  CLAIM_TYPE_DETAILS: FnolStepsObj;
  PAYMENT_DETAILS: FnolStepsObj;
  UPLOAD_DOCS: FnolStepsObj;
  DECLARATION: FnolStepsObj;
}

interface FnolLiteAlternateFlowConfig {
  countries: string[];
  triggerField?: string;
  triggerValue?: string;
  triggerConditions?: Record<string, any>;
  stepKeys: (keyof FnolLiteStepList)[];
  jumpToStepKey: keyof FnolLiteStepList;
}

export const FNOL_STEPS_SINGAPORE: FnolSteps = {
  CLAIMANT_INFO: {
    name: "fnol-claimant-info",
    slNo: 1,
    title: `Personal Information`,
    progressStepTitle: `Personal Information`,
  },
  CLAIM_TYPE_DETAILS: {
    name: "fnol-claimtype-details",
    slNo: 2,
    title: `Claim Details`,
    progressStepTitle: `Claim Details`,
  },
  PAYMENT_DETAILS: {
    name: "fnol-payment-details",
    slNo: 3,
    title: `Payment Details`,
    progressStepTitle: `Payment`,
  },
  UPLOAD_DOCS: {
    name: "fnol-upload-docs",
    slNo: 4,
    title: ` Documents`,
    progressStepTitle: `Documents`,
  },
  DECLARATION: {
    name: "fnol-declaration",
    slNo: 5,
    title: `Review and Submit the Claim`,
    progressStepTitle: `Review & Submit`,
  },
};

export const FNOL_STEPS_LITE = () => {
  return {
    list: {
      CLAIMANT_INFO: {
        name: "fnol-claimant-info",
        slNo: 1,
        title: `Personal Information`,
        progressStepTitle: $localize`:@@common.personalInfo:Personal Information`,
      },
      CLAIM_TYPE_DETAILS: {
        name: "fnol-claimtype-details",
        slNo: 2,
        title: `Claim Details`,
        progressStepTitle: $localize`:@@common.claimDetails:Claim Details`,
      },
      PAYMENT_DETAILS: {
        name: "fnol-payment-details",
        slNo: 3,
        title: `Payment Details`,
        progressStepTitle: $localize`:@@common.payment:Payment`,
      },
      UPLOAD_DOCS: {
        name: "fnol-upload-docs",
        slNo: 4,
        title: ` Documents`,
        progressStepTitle: $localize`:@@common.documents:Documents`,
      },
      DECLARATION: {
        name: "fnol-declaration",
        slNo: 5,
        title: `Review and Submit the Claim`,
        progressStepTitle: $localize`:@@common.review:Review & Submit`,
      },
   }
  }
};

export const FNOL_LITE_ALTERNATE_FLOWS: FnolLiteAlternateFlowConfig[] = [
  {
    countries: ["gb"],
    triggerConditions: {
      additionalSupportClaim: "Yes",
      proceedWithClaim: "requestClaimsTeam",
    },
    stepKeys: ["CLAIMANT_INFO", "DECLARATION"],
    jumpToStepKey: "DECLARATION",
  }
];

export const getFnolLiteAlternateFlowConfig = (
  country: string,
  uiData: Record<string, any> = {}
): FnolLiteAlternateFlowConfig | undefined => {
  const normalizedCountry = country?.toLowerCase();

  return FNOL_LITE_ALTERNATE_FLOWS.find(({ countries, triggerField, triggerValue, triggerConditions }) => {
    if (!countries.includes(normalizedCountry)) {
      return false;
    }

    if (triggerConditions) {
      return Object.entries(triggerConditions).every(([field, value]) => uiData?.[field] === value);
    }

    return triggerField !== undefined && uiData?.[triggerField] === triggerValue;
  });
};

export const getFnolLiteStepList = (
  country: string,
  uiData: Record<string, any> = {}
): FnolStepsObj[] => {
  const fnolSteps = FNOL_STEPS_LITE().list;
  const alternateFlowConfig = getFnolLiteAlternateFlowConfig(country, uiData);

  if (!alternateFlowConfig) {
    return Object.values(fnolSteps);
  }

  return alternateFlowConfig.stepKeys.map((stepKey) => fnolSteps[stepKey]);
};

export const getFnolLiteJumpStep = (
  country: string,
  uiData: Record<string, any> = {}
): FnolStepsObj | undefined => {
  const alternateFlowConfig = getFnolLiteAlternateFlowConfig(country, uiData);

  if (!alternateFlowConfig) {
    return undefined;
  }

  return FNOL_STEPS_LITE().list[alternateFlowConfig.jumpToStepKey];
};

export const ContainerCountMapSG = {
  addTreatmentMain: 6,
  addFractureMain: 6,
  addDislocationMain: 6,
  addFractureTravelMain: 6,
  addDislocationTravelMain: 6,
  addLeaveMain: 6,
  addLeaveTravelMain: 6,
  addDoctorMain: 6,
  addDoctorTravelMain: 6,
  addWitnessMain: 6,
  addIncomeMain: 6,
  addItemsMain: 6,
  addStolenMain: 6,
  addEntryMain: 6,
  addPhysicianMain: 6,
  addCriticalTreatmentMain: 6,
  addMedicalExpenseMain: 6,
  addMedicalExpenseTravelMain: 6,
  addHospitalCashMain: 6,
  addDelayedFlightMain: 6,
  addWasSickTreatmentMain: 6,
  addWasInjuredTreatmentTypeMain: 6,
  addInsuranceDetailsMain: 6,
  addHospitalisationMain: 6,
  hospitalisedBySickMain: 6,
  hospitalisedByInjuryMain: 6,
  addDriversRentalVehicleMain: 6
};

export const DATE_FORMAT = "dd/MM/yyyy";
export const CLAIM_TYPE_DATE_FIELDS = [
  "dateAccident",
  "firstVisit",
  "dateOfAdmission",
  "dateOfDischarge",
  "dateOfStart",
  "dateOfDisability",
  "dateOfDeath",
  "dateOfDeathTravel",
  "dateSymptom",
  "dateVisit",
  "dateOfAdmissionIllness",
  "dateOfDischargeIllness",
  "dateOfDiagnosed",
  "dateOfonset",
  'dateOfvisit',
  'criticalIllnessAdmissionDate',
  'CriticalIllnessDischargeDate',
  'dateOfAdmissionHospitalCash',
  'dateOfDischargeHospitalCash'
];

export const SG_DATA_CONTAINERS = [
  "addTreatmentMain",
  "addFractureMain",
  "addDislocationMain",
  "addFractureTravelMain",
  "addDislocationTravelMain",
  "addLeaveMain",
  "addLeaveTravelMain",
  "addDoctorMain",
  "addDoctorTravelMain",
  "addWitnessMain",
  "addIncomeMain",
  "addPhysicianMain",
  "addCriticalTreatmentMain",
  "addHospitalCashMain",
  "addDelayedFlightMain"
];

export const HEADINGMANAGERSG = {
  claimTypeAs: [
    "claimTypeAs",
    "others",
    "firstName",
    "lastName",
    "insuredDOB",
    "insuredNric",
    "insuredMobileNumber",
    "insuredEmail",
    "nric",
    "nonInsuredMobileNumber",
    "email",
    "dob",
    "currentAge",
    "country",
    "myAddressLookup",
    "levelUnitNumber",
    "postalCode",
    "insuredCountry",
  ],
  nameBroker: ["nameBroker", "emailBroker", "brokerMobileNumber"],
  accidentOccurred: [
    "accidentOccurred",
    "injuriesSustained",
    "dateAccident",
    "timeIncident",
    "accidentLocationLookup",
    "firstVisit",
    "diagnosis",
    "others",
    "trafficAccident",
  ],
  chooseClaimType: ["chooseClaimType"],
  medicalExpense: ["medicalExpense", "citizen"],
  admittedHospital: [
    "admittedHospital",
    "addTreatmentMain",
    "admittedIcu",
    "admittedToIcu",
  ],
  criticalIllnessHospital: [
    "criticalIllnessHospital",
    "addCriticalTreatmentMain",
    "criticalIllnessHospitalIcu",
    "criticalIllnessHospitalToIcu",
  ],
  hospitalCash: [
    "addHospitalCashMain",
    "addHospitalCashIcu",
    "addHospitalCashToIcu",
  ],
  sustainFracture: [
    "sustainFracture",
    "addFractureMain",
    "sustainDislocation",
    "addDislocationMain",
  ],
  addLeaveMain: ["addLeaveMain", "employed", "dateOfExpected"],
  dateOfDisability: ["dateOfDisability", "typeDisability"],
  dateOfDeath: [
    "dateOfDeath",
    "localTimePersonal",
    "causeDeath",
    "causeOthers",
    "autopsy",
    "reason",
  ],
  doctorInfo: ["doctorInfo", "addDoctorMain"],
  doctorInfoTravel: ["doctorInfoTravel", "addDoctorTravelMain"],
  physicianInfo: ["physicianInfo", "addPhysicianMain"],
  witnessInfo: ["witnessInfo", "addWitnessMain"],
  describeIllness: [
    "describeIllness",
    "dateSymptom",
    "dateVisit",
    "diagnosisIllness",
    "othersIllness",
    "claimingIllness",
  ],
  chooseClaimTypeIllness: ["chooseClaimTypeIllness"],
  dailyIncome: ["addIncomeMain", "admittedIcuIllness", "admittedToIcuIllness"],
  dateOfDiagnosed: ["dateOfDiagnosed", "disabilityIllness"],
  paymentChannel: [
    "paymentChannel",
    "payeeNameBank",
    "bankName",
    "accountNumber",
    "confirmAccountNumber",
    "payeeName",
  ],
  customFileUpload: ["customFileUpload"],
};


export class UtilsConstSGLabelsRetrieve {
  public static getClaimBenefitTypes(benefitType): Array<any> {
    let claimBenefitType = [];
    if (benefitType === "Personal Accident") {
      claimBenefitType = [
        {
          id: "1",
          name: "Copy of Death Certificate",
        },
        {
          id: "2",
          name: "Medical Bill And Receipts",
        },
        {
          id: "3",
          name: "Medical Leave Certificate",
        },
        {
          id: "4",
          name: "Medical Report/Doctor's Memo/Inpatient Discharge Summary",
        },
        {
          id: "5",
          name: "Medical Reports",
        },
        {
          id: "6",
          name: "Police Report If This Is A Road Traffic Accident",
        },
        {
          id: "7",
          name: "Postmortem And Toxicology Report",
        },
        {
          id: "8",
          name: "Referral Letter For Physiotherapy Treatment",
        },
      ];
    } else if (benefitType === "Illness / Critical Illness") {
      claimBenefitType = [
        {
          id: "1",
          name: "All Available Functional Test Results",
        },
        {
          id: "2",
          name: "All Available Test/Laboratory Results",
        },
        {
          id: "3",
          name: "Detailed Medical Report",
        },
        {
          id: "4",
          name: "Hospitalisation Bills",
        },
        {
          id: "5",
          name: "Medical Report/Inpatient discharge summary/Attending physician's statement by treating doctor",
        },
      ];
    }
    return claimBenefitType;
  }
}
export class UtilsConstLITELabelsRetrieve {
  public static getClaimBenefitTypes(benefitType): Array<any> {
    let claimBenefitType = [];
    if (benefitType === "Personal Accident") {
      claimBenefitType = [
        {
          id: "1",
          name: $localize`:@@documents.copyOfDC:Copy of Death Certificate`,
        },
        {
          id: "2",
          name: $localize`:@@documents.medicalBill:Medical Bill And Receipts`,
        },
        {
          id: "3",
          name: $localize`:@@documents.medicalLeave:Medical Leave Certificate`,
        },
        {
          id: "4",
          name: $localize`:@@documents.doctorMemo:Medical Report/Doctor's Memo/Inpatient Discharge Summary`,
        },
        {
          id: "5",
          name: $localize`:@@documents.medicalReport:Medical Reports`,
        },
        {
          id: "6",
          name: $localize`:@@documents.policeReport:Police Report If This Is A Road Traffic Accident`,
        },
        {
          id: "7",
          name: $localize`:@@documents.postmortem:Postmortem And Toxicology Report`,
        },
        {
          id: "8",
          name: $localize`:@@documents.referralLetter:Referral Letter For Physiotherapy Treatment`,
        },
      ];
    } else if (benefitType === "Travel") {
      claimBenefitType = [
        {
          id: "1",
          name: $localize`:@@documents.ackReceipt:Acknowledgement receipt on date and time baggage received`,
        },
        {
          id: "2",
          name: $localize`:@@documents.airlineLetter:Airline Letter on the flight delay`,
        },
        {
          id: "3",
          name: $localize`:@@documents.originalInvoice:Booking Confirmation/Original Invoice`,
        },
        {
          id: "4",
          name: $localize`:@@documents.copy:Copy of death Certificate (Where applicable)`,
        },
        {
          id: "5",
          name: $localize`:@@documents.passport:Copy of passport and travel itinerary`,
        },
        {
          id: "6",
          name: $localize`:@@documents.passportSummary:Doctor's memo/medical report/inpatient discharge summary`,
        },
        {
          id: "7",
          name: $localize`:@@documents.refundLetter:Letter of refund`,
        },
        {
          id: "8",
          name: $localize`:@@documents.travelmemo:Medical report/memo from the doctors advising the fitness to travel`,
        },
        {
          id: "9",
          name: $localize`:@@documents.orgReceipt:Original medical bills and receipts`,
        },
        {
          id: "10",
          name: $localize`:@@documents.orgBill:Original phone bills (For Emergency Telephone Charges benefit)`,
        },
        {
          id: "11",
          name: $localize`:@@documents.orgDocument:Original receipts for replacement/temporary travel documents`,
        },
        {
          id: "12",
          name: $localize`:@@documents.orgAccomodation:Original transportations and accommodation receipts in obtaining replacement travel documents`,
        },
        {
          id: "13",
          name: $localize`:@@documents.policeReportTravel:Police Report`,
        },
        {
          id: "14",
          name: $localize`:@@documents.proof:Proof of relationship to insured`,
        },
        {
          id: "15",
          name: $localize`:@@documents.flyerPoint:Proof of unused entertainment ticket/frequent flyer points (for Disruption Benefit)`,
        },
        {
          id: "16",
          name: $localize`:@@documents.irrReport:Property irregularity report`,
        },
        {
          id: "17",
          name: $localize`:@@documents.boardingPass:Travel itineraries and Boarding Pass`,
        }
      ];
    }
    else {
      claimBenefitType = [
        {
          id: "1",
          name: $localize`:@@documents.otherDocuments:Other Documents`,
        }
      ]
    }
    return claimBenefitType;
  }
}

export const FRACTURELOCATION = [
  {
    typeId: "1",
    locations: [
      {
        id: "2",
        name: "Compound",
      },
      {
        id: "3",
        name: "All other fractures",
      },
    ],
  },
  {
    typeId: "2",
    locations: [
      {
        id: "1",
        name: "Complete",
      },
      {
        id: "2",
        name: "Compound",
      },
      {
        id: "3",
        name: "All other fractures",
      },
    ],
  },
  {
    typeId: "3",
    locations: [
      {
        id: "1",
        name: "Complete",
      },
      {
        id: "2",
        name: "Compound",
      },
      {
        id: "3",
        name: "All other fractures",
      },
    ],
  },
  {
    typeId: "4",
    locations: [
      {
        id: "1",
        name: "Complete",
      },
      {
        id: "2",
        name: "Compound",
      },
      {
        id: "3",
        name: "All other fractures",
      },
    ],
  },
  {
    typeId: "5",
    locations: [
      {
        id: "1",
        name: "Complete",
      },
      {
        id: "2",
        name: "Compound",
      },
      {
        id: "3",
        name: "All other fractures",
      },
    ],
  },
  {
    typeId: "6",
    locations: [
      {
        id: "2",
        name: "Compound",
      },
      {
        id: "3",
        name: "All other fractures",
      },
    ],
  },
  {
    typeId: "7",
    locations: [
      {
        id: "1",
        name: "Complete",
      },
      {
        id: "2",
        name: "Compression",
      },
      {
        id: "3",
        name: "All other fractures",
      },
    ],
  },
  {
    typeId: "8",
    locations: [
      {
        id: "1",
        name: "Complete",
      },
      {
        id: "2",
        name: "Compound",
      },
      {
        id: "3",
        name: "All other fractures",
      },
    ],
  },
];
